import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CalendarIntervalDay } from 'platform-components';
import React, { RefObject, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ym from 'react-yandex-metrika';

import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { LessonsLikesPopup } from 'common.components/lessons/__lesson-likes-popup/lessons__lesson-likes-popup';
import Lesson from 'common.components/lessons/__lesson/lessons__lesson';
import { getClessonResults, getClessonsCriteriaMarks } from 'store/clesson-results/selectors';
import { ReduxClessonResultsStore } from 'store/clesson-results/typings';
import { getClessonProgress } from 'store/clesson-results/utils';
import { getCourseByClassAndId } from 'store/courses/selectors';
import * as likesSelectors from 'store/likes/selectors';
import { ReduxLikesStoreState } from 'store/likes/typings';

import { getCanEditLessons } from '../../../course-view/selectors/course.selectors';
import { getCourseCalendarEditor } from '../../selectors';
import { CourseCalendarColumnsCard } from '../../typings';
import { getMetrikaWeek } from '../../utils';

interface CourseCalendarCardTypeClessonProps extends CourseCalendarColumnsCard {
    editorId: string;
    teacherId?: number;
    forceCompleteLessonSuccess?: (day: CalendarIntervalDay) => void;
    day: CalendarIntervalDay;
}

export const CourseCalendarCardTypeClesson = ({
    editorId,
    cardId,
    teacherId,
    forceCompleteLessonSuccess,
    day,
}: CourseCalendarCardTypeClessonProps) => {
    const dispatch = useDispatch();
    const { classId = '', subject = '' } = useParams<{ classId?: string; subject?: string }>();
    const { clessons, currentInterval } = useSelector((store) => getCourseCalendarEditor(store, editorId));
    const { clesson, courseId } = clessons[cardId];
    const clessonResult = useSelector((store: ReduxClessonResultsStore) => getClessonResults(store)[clesson.id]);
    const criteriaMarks = useSelector((store: ReduxClessonResultsStore) => getClessonsCriteriaMarks(store));
    const canEditLessons = useSelector((store) => getCanEditLessons(store));
    const clessonLikes = useSelector((store: ReduxLikesStoreState) => likesSelectors.getClessonTotalLikes(store));
    const course = useSelector((store) => getCourseByClassAndId(store, { classId, courseId }));
    const shouldShowLikes = useSelector((store) =>
        likesSelectors.getShouldShowLikeReactions(store, { classId, subject })
    );
    const likesCount = clessonLikes?.[clesson.id] || 0;

    const [likePopup, updateLikePopup] = useState<{
        visible: boolean;
        likeRef?: RefObject<HTMLElement>;
        clessonId?: number;
    }>({
        visible: false,
    });

    let clessonProgressData;

    if (clessonResult?.loadingStatus === LOADING_STATUSES.SUCCESS) {
        clessonProgressData = getClessonProgress(clessonResult.data, criteriaMarks, clesson);
    }

    const handleShowLikesPopup = useCallback(
        (clessonId: number, likeRef: React.Ref<HTMLElement>) => {
            updateLikePopup({
                clessonId,
                likeRef: likeRef as React.RefObject<HTMLElement>,
                visible: true,
            });

            ym('reachGoal', 'teacher_navigation_calendar_lessons_show_likes', {
                teacherID: teacherId,
                teamID: classId,
                clesson_id: clessonId,
                week: getMetrikaWeek(currentInterval[0].date, currentInterval[currentInterval.length - 1].date),
            });
        },
        [teacherId, classId, currentInterval]
    );

    const handleHideLikesPopup = useCallback(() => {
        updateLikePopup({ visible: false });
    }, []);

    const handleForceCompleteLessonSuccess = useCallback(() => {
        if (forceCompleteLessonSuccess) {
            forceCompleteLessonSuccess(day);
        }
    }, [forceCompleteLessonSuccess, day]);

    const handleLessonClick = useCallback(() => {
        dispatch(lessonViewActions.toggleModal(true));
    }, [dispatch]);

    if (!course) {
        return null;
    }

    return (
        <>
            {/* @ts-ignore - EDUCATION-37077: [react-update]: fixme typings: потерян обяз пропс io */}
            <Lesson
                clesson={clesson}
                clessonProgress={clessonProgressData}
                clessonProgressLoadingStatus={clessonResult?.loadingStatus}
                course={course}
                courseId={courseId}
                forceCompleteLessonSuccess={handleForceCompleteLessonSuccess}
                isListView={false}
                likesCount={likesCount}
                onHideLikesPopup={handleHideLikesPopup}
                onLessonClick={handleLessonClick}
                onShowLikesPopup={handleShowLikesPopup}
                shouldShowLikes={shouldShowLikes}
                shouldShowSettings={canEditLessons}
            />
            {likePopup.visible && likePopup.clessonId && (
                <LessonsLikesPopup
                    anchorRef={likePopup.likeRef}
                    clessonId={likePopup.clessonId}
                    onClose={() => ({})}
                    visible={likePopup.visible}
                />
            )}
        </>
    );
};
